import { startOfDay } from 'date-fns';
import React, { RefObject, useCallback } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { css } from '@tanium/react-emotion-9';
import { useTheme } from '@tanium/react-theme-context';
import { Theme, ThemeShadows } from '@tanium/theme-data';
import { useTranslation } from './localization';
import {
  ContainerFill,
  DayBorder,
  DayBorderNotInMonth,
  DayFill,
  DayFillActive,
  DayFillHover,
  DayFillNotInMonth,
  DayFillSelected,
  DaysOfWeekHeaderText,
  DayText,
  DayTextActive,
  DayTextDisabled,
  DayTextNotInMonth,
  MinMaxHighlightCorner,
} from './themeDefinitions';

type CalendarContainerFactory = (
  setDateValue: (newDate: Date) => void,
  ref: RefObject<Pick<ReactDatePicker, 'setFocus' | 'setOpen'>>,
  isValidDay: (date: Date) => boolean,
) => NonNullable<ReactDatePickerProps['calendarContainer']>;

type Interpolation = Parameters<typeof css>[0];

/**
 * Stylesheet for ReactDatePicker's calendar popover. We don't actually import the styles
 * ReactDatePicker ships with; instead, we create a scoped stylesheet.
 */
// @ts-expect-error -- mismatch between csstype versions used by `theme-data` and `react-emotion-9`
const datePickerStyleSheet: (theme: Theme) => Interpolation = (theme) => {
  const { borders, fontWeights, radii, space } = theme;

  return {
    // Months
    '.react-datepicker__month-container': {
      display: 'table',
    },

    // Weeks
    '.react-datepicker__week': {
      display: 'block',
      marginBottom: space[1],
    },

    // Days / Day Names (common)
    '.react-datepicker__day, .react-datepicker__day-name': {
      border: borders[1],
      borderRadius: 4,
      display: 'inline-block',
      lineHeight: `16px`,
      marginRight: space[1],
      textAlign: 'center',
      verticalAlign: 'middle',
      width: 36,
    },
    '.react-datepicker__day:last-child, .react-datepicker__day-name:last-child': {
      marginRight: 0,
    },

    // Day names
    '.react-datepicker__day-name': {
      borderColor: 'transparent',
      height: 16,
      marginBottom: space[1],
    },

    // Days
    '.react-datepicker__day': {
      backgroundColor: DayFill(theme),
      borderColor: DayBorder(theme),
      color: DayText(theme),
      cursor: 'pointer',
      height: 32,
      lineHeight: `32px`,
      ':not(.react-datepicker__day--selected):not(.react-datepicker__day--disabled)': {
        ':hover': {
          background: DayFillHover(theme),
        },
        ':active': {
          backgroundColor: DayFillActive(theme),
          color: DayTextActive(),
        },
      },
    },
    '.react-datepicker__day--selected': {
      backgroundColor: DayFillSelected(),
      borderColor: DayFillSelected(),
      color: DayTextActive(),
      fontWeight: fontWeights.bold,
    },

    '.react-datepicker__day--outside-month': {
      backgroundColor: DayFillNotInMonth(theme),
      borderColor: DayBorderNotInMonth(theme),
      color: DayTextNotInMonth(theme),
      ':hover:not(.react-datepicker__day--disabled)': {
        color: DayText(theme),
      },
    },
    '.react-datepicker__day--keyboard-selected': {
      background: DayFillHover(theme),
    },
    '.react-datepicker__day--disabled': {
      color: DayTextDisabled(theme),
      cursor: 'not-allowed',
      position: 'relative',
      '::before': {
        position: 'absolute',
        content: '""',
        width: 0,
        height: 0,
        top: -1,
        left: -1,
        borderStyle: 'solid',
        borderWidth: 6,
        borderColor: `${MinMaxHighlightCorner(
          theme,
        )} transparent transparent ${MinMaxHighlightCorner(theme)}`,
        borderTopLeftRadius: radii[1],
      },
    },
  };
};

/**
 * Factory function for the calendar popover used in the DatePicker. Responsibilities include
 * ensuring the correct styles are applied to the DatePicker subcomponents and providing
 * a "Today" selector button at the bottom of the calendar for user convenience.
 */
export const calendarContainerFactory: CalendarContainerFactory =
  (setDateValue, datePickerRef, isValidDay) =>
  ({ children }) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { setOpen } = datePickerRef.current!;
    const theme = useTheme();
    // We are passing in Date.now for ease of testing
    const today = startOfDay(new Date(Date.now()));
    const todayIsValid = isValidDay(today);
    const { t } = useTranslation();

    const handleTodayClick = useCallback(() => {
      setDateValue(today);
      setOpen(false);
    }, [setOpen, today]);

    return (
      <Box
        color={DaysOfWeekHeaderText}
        px={2}
        bg={ContainerFill}
        borderRadius={1}
        display="inline-block"
        className={css(datePickerStyleSheet(theme))}
        pt={2}
        pb={1}
        boxShadow={ThemeShadows.Overlay2}
      >
        {children}
        {todayIsValid && (
          <Box display="flex" justifyContent="flex-end" my={1}>
            <Button variant="link" onClick={handleTodayClick}>
              {t('Today')}
            </Button>
          </Box>
        )}
      </Box>
    );
  };
